import { useEffect, useMemo, useRef, useState } from "react";
import maplibregl, { type Map as MapLibreMap, Marker } from "maplibre-gl";
import Fab from "@mui/material/Fab";
import Tooltip from "@mui/material/Tooltip";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import SatelliteAltOutlinedIcon from "@mui/icons-material/SatelliteAltOutlined";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import NavigationIcon from "@mui/icons-material/Navigation";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useAppState } from "../state/AppState";
import { useLang } from "../i18n/LangContext";
import type { Spot } from "../types";
import { spotSourceKey } from "../state/filters";

const ICELAND_BOUNDS: [[number, number], [number, number]] = [
  [-24.7, 63.3],
  [-13.3, 66.7],
];

type BasemapKey = "streets" | "satellite";

const BASEMAPS: Record<
  BasemapKey,
  { style: maplibregl.StyleSpecification | string; attribution: string }
> = {
  streets: {
    style: "https://tiles.openfreemap.org/styles/positron",
    // Exact attribution required by openfreemap.org/#attribution:
    // OpenFreeMap (tile server) + OpenMapTiles (vector schema) +
    // OpenStreetMap (data). All three links are mandatory.
    attribution:
      '<a href="https://openfreemap.org" target="_blank" rel="noopener">OpenFreeMap</a> <a href="https://www.openmaptiles.org/" target="_blank" rel="noopener">&copy; OpenMapTiles</a> Data from <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a>',
  },
  satellite: {
    style: {
      version: 8,
      sources: {
        sat: {
          type: "raster",
          tiles: [
            "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
          ],
          tileSize: 256,
          maxzoom: 19,
        },
      },
      layers: [{ id: "sat", type: "raster", source: "sat" }],
    },
    // Exact `accessInformation` from Esri's World_Imagery item metadata
    // (arcgis.com/sharing/rest/content/items/10df2279…). Maxar was
    // rebranded to Vantor in 2025; the four-name source list is the
    // legal minimum per Esri's ToS.
    attribution:
      'Source: Esri, Vantor, Earthstar Geographics, and the GIS User Community',
  },
};

function markerKind(spot: Spot): "free" | "card" | "default" {
  if (spot.isFree) return "free";
  return spot.prices.some((p) => p.unit === "per_card_year") ? "card" : "default";
}

const ICON_FISH =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M2 12c3-4 6-6 11-6 4 0 7 2 9 6-2 4-5 6-9 6-5 0-8-2-11-6Z"/><path d="M14 9.5l3-2.5M14 14.5l3 2.5"/><circle cx="17" cy="11.5" r=".7" fill="currentColor"/></svg>';
const ICON_TICKET =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M2 9V5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v4a2 2 0 0 0 0 6v4a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-4a2 2 0 0 0 0-6Z"/></svg>';

function buildMarkerEl(spot: Spot): HTMLElement {
  const kind = markerKind(spot);
  const inner = kind === "card" ? ICON_TICKET : ICON_FISH;
  const srcKey = spot.isFree ? "free" : spotSourceKey(spot);
  const cssSrc = srcKey === "free" ? "free" : srcKey.replace(".is", "");
  const wrapper = document.createElement("div");
  wrapper.className = `marker marker--${kind} marker--src-${cssSrc}`;
  wrapper.title = spot.name;
  wrapper.innerHTML = `<div class="marker__inner">${inner}</div>`;
  return wrapper;
}

export function MapView() {
  const { visibleSpots, selectSpot, selectedId } = useAppState();
  const { lang, t } = useLang();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const markersRef = useRef<Map<string, Marker>>(new Map());
  const [basemap, setBasemap] = useState<BasemapKey>(() => {
    const stored = typeof localStorage !== "undefined" ? localStorage.getItem("fishing-basemap") : null;
    return stored === "streets" ? "streets" : "satellite";
  });

  // Init the map once.
  useEffect(() => {
    if (!containerRef.current) return;
    let map: MapLibreMap | null = null;
    try {
      map = new maplibregl.Map({
        container: containerRef.current,
        style: BASEMAPS[basemap].style,
        bounds: ICELAND_BOUNDS,
        fitBoundsOptions: { padding: 16 },
        attributionControl: false,
      });
    } catch (err) {
      console.warn("MapLibre init failed", err);
      return;
    }
    // No zoom controls at all — pinch-to-zoom on touch and the scroll
    // wheel / trackpad pinch on desktop are enough, and the +/- group
    // clutters every viewport.
    // No built-in attribution: we render our own minimal OSM notice
    // outside the map so MapLibre can't auto-inject the source-level
    // OpenMapTiles / OpenFreeMap strings.
    mapRef.current = map;

    return () => {
      markersRef.current.forEach((m) => m.remove());
      markersRef.current.clear();
      map?.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Swap basemap when the toggle changes.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    map.setStyle(BASEMAPS[basemap].style);
    try {
      localStorage.setItem("fishing-basemap", basemap);
    } catch {
      /* ignore */
    }
  }, [basemap]);

  // Render markers each time the visible set changes. Groups spots that
  // share the same coord and fans them out around a small circle so each
  // is independently clickable. Spots without coordinates are dropped here
  // — they still appear in the sidebar list, just not on the map.
  const grouped = useMemo(() => {
    const groups = new Map<string, Spot[]>();
    for (const spot of visibleSpots) {
      if (spot.lat == null || spot.lon == null) continue;
      const key = `${spot.lat.toFixed(3)},${spot.lon.toFixed(3)}`;
      const list = groups.get(key) ?? [];
      list.push(spot);
      groups.set(key, list);
    }
    return groups;
  }, [visibleSpots]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    markersRef.current.forEach((m) => m.remove());
    markersRef.current.clear();

    const FAN_RADIUS = 0.0009;
    for (const [, spots] of grouped) {
      if (spots.length === 1) {
        const spot = spots[0];
        const el = buildMarkerEl(spot);
        el.addEventListener("click", (ev) => {
          ev.stopPropagation();
          selectSpot(spot.id);
        });
        const marker = new maplibregl.Marker({ element: el, anchor: "center" })
          .setLngLat([spot.lon, spot.lat])
          .addTo(map);
        markersRef.current.set(spot.id, marker);
        continue;
      }
      const sorted = [...spots].sort((a, b) => a.id.localeCompare(b.id));
      sorted.forEach((spot, i) => {
        const angle = (2 * Math.PI * i) / sorted.length - Math.PI / 2;
        const offLat = FAN_RADIUS * Math.sin(angle);
        const offLon = (FAN_RADIUS * Math.cos(angle)) / Math.cos((spot.lat * Math.PI) / 180);
        const el = buildMarkerEl(spot);
        el.addEventListener("click", (ev) => {
          ev.stopPropagation();
          selectSpot(spot.id);
        });
        const marker = new maplibregl.Marker({ element: el, anchor: "center" })
          .setLngLat([spot.lon + offLon, spot.lat + offLat])
          .addTo(map);
        markersRef.current.set(spot.id, marker);
      });
    }
  }, [grouped, selectSpot]);

  // No automatic camera moves on spot select / close — the user keeps
  // whatever view they had. The list-row stays the only "navigate to
  // this spot" affordance.

  // When the right-side detail panel is open on desktop, shift the
  // basemap switcher leftwards by the panel width. We use the same
  // 250ms ease as the panel's slide animation (CSS `transition: right`
  // on .basemap-switch), so both elements move together.
  const isMobile =
    typeof window !== "undefined" && window.matchMedia("(max-width: 900px)").matches;
  const detailW =
    typeof getComputedStyle !== "undefined"
      ? parseInt(
          getComputedStyle(document.documentElement).getPropertyValue("--detail-w"),
          10,
        ) || 440
      : 440;
  const panelOffset = selectedId && !isMobile ? detailW : 0;

  // Track map bearing so we can show a compass button when the user
  // rotates the map away from north.
  const [bearing, setBearing] = useState(0);
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const onRotate = () => setBearing(map.getBearing());
    map.on("rotate", onRotate);
    return () => {
      map.off("rotate", onRotate);
    };
  }, []);

  const toggleBasemap = () =>
    setBasemap((b) => (b === "streets" ? "satellite" : "streets"));
  const resetNorth = () => {
    mapRef.current?.easeTo({ bearing: 0, pitch: 0, duration: 300 });
  };

  const fabRight = 16 + panelOffset;

  return (
    <main id="map" ref={containerRef}>
      {/* Layer-style mini-FAB (Google-Maps-style): toggles base map. */}
      <Tooltip
        title={
          basemap === "streets" ? t("basemapSatellite") : t("basemapStreets")
        }
        placement="left"
      >
        <Fab
          size="small"
          color="default"
          onClick={toggleBasemap}
          aria-label={
            basemap === "streets" ? t("basemapSatellite") : t("basemapStreets")
          }
          sx={{
            position: "absolute",
            // Sits flush at the top of the map cell. Since the Topbar
            // is fixed and accounts for its own safe-area-inset-top
            // padding, the map cell already starts below it — so 16px
            // is all the clearance needed on every viewport.
            top: 16,
            right: `calc(${fabRight}px + var(--sar))`,
            zIndex: 10,
            bgcolor: "background.paper",
            color: "text.primary",
            transition: "right 0.25s ease",
            "&:hover": { bgcolor: "background.paper" },
          }}
        >
          {basemap === "streets" ? <SatelliteAltOutlinedIcon /> : <MapOutlinedIcon />}
        </Fab>
      </Tooltip>
      {/* Compass: only visible when the map is not pointing north. */}
      {Math.abs(bearing) > 0.5 && (
        <Tooltip title={t("resetNorth")} placement="left">
          <Fab
            size="small"
            color="default"
            onClick={resetNorth}
            aria-label={t("resetNorth")}
            sx={{
              position: "absolute",
              // Directly below the basemap-switch FAB (same x, 16 +
              // 40px FAB + 16 gap = 72).
              top: 72,
              right: `calc(${fabRight}px + var(--sar))`,
              zIndex: 10,
              bgcolor: "background.paper",
              color: "text.primary",
              transition: "right 0.25s ease",
              "&:hover": { bgcolor: "background.paper" },
            }}
          >
            <NavigationIcon sx={{ transform: `rotate(${-bearing}deg)` }} />
          </Fab>
        </Tooltip>
      )}
      <MapAttribution map={mapRef.current} basemap={basemap} />
      {/* lang kept in deps so basemap labels rerender on language switch */}
      <span style={{ display: "none" }} data-lang={lang} />
    </main>
  );
}

/** Basemap attribution.
 *  - Visible by default so the credits show on load.
 *  - Collapses to a discreet "i" icon once the user starts panning/zooming
 *    (or after 4s grace), to free up the map area.
 *  - Click on the icon re-expands the full text.
 *  - Text comes from the active basemap so satellite credits Esri/Maxar,
 *    streets credits OpenFreeMap/OSM. */
function MapAttribution({
  map,
  basemap,
}: {
  map: MapLibreMap | null;
  basemap: BasemapKey;
}) {
  const [open, setOpen] = useState(true);
  useEffect(() => {
    if (!map) return;
    const collapse = () => setOpen(false);
    map.on("movestart", collapse);
    // Also auto-collapse after a short grace period if the user hasn't
    // interacted yet — keeps the initial banner brief.
    const t = window.setTimeout(collapse, 4000);
    return () => {
      map.off("movestart", collapse);
      window.clearTimeout(t);
    };
  }, [map]);
  // Re-show the credits briefly when the user switches basemaps so the
  // new provider's notice is visible at least once.
  useEffect(() => {
    setOpen(true);
    const t = window.setTimeout(() => setOpen(false), 4000);
    return () => window.clearTimeout(t);
  }, [basemap]);

  const attrHtml = BASEMAPS[basemap].attribution;

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <div className={"map-attribution" + (open ? " is-open" : "")}>
        {open ? (
          <button
            type="button"
            className="map-attribution__text"
            onClick={(e) => {
              // Let clicks land on the embedded <a> tags; only collapse
              // when the surrounding button itself is the target.
              if (e.target === e.currentTarget) setOpen(false);
            }}
            aria-label="Hide attribution"
            dangerouslySetInnerHTML={{ __html: attrHtml }}
          />
        ) : (
          <button
            type="button"
            className="map-attribution__toggle"
            aria-label="Attribution"
            onClick={() => setOpen(true)}
          >
            <InfoOutlinedIcon fontSize="inherit" />
          </button>
        )}
      </div>
    </ClickAwayListener>
  );
}
